import { Link, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilChevronLeft, cilChevronRight } from "@coreui/icons";
import type { ApiError } from "../../api/client";
import {
  TradingStrategySignalAction,
  useTradingStrategySignal,
  type RelatedTradingStrategySignalDto,
  type TradingStrategySignalDetailDto,
} from "../../api/hooks/useSignals";
import { formatDateTimeFull, formatDayMonth, formatSignalTime } from "../../lib/dates";
import { heldFor, openFor, signalActionLabel } from "../../lib/signal-copy";
import {
  customerHasActiveSubscription,
  useMySubscriptions,
} from "../../api/hooks/useStrategySubscriptions";
import { myStrategyDetailPath, routePaths, signalDetailPath } from "../../routes/route-paths";
import { SignalActionBadge } from "./components/SignalActionBadge";
import { SignalRow } from "./components/SignalRow";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { StrategySideLabel } from "../trading-strategies/components/StrategySideLabel";

/**
 * One signal: where its strategy stands, its facts, the trade it opens or closes, and the strategy's
 * signals around it. It describes the strategy and never tells the customer what to do: someone
 * following two strategies that trade the same instrument holds one position neither signal describes.
 * Signals carry no price, so there is no gain or loss to show.
 */
export function SignalDetail() {
  const { t } = useTranslation("signals");
  const { signalId = "" } = useParams<{ signalId: string }>();
  const query = useTradingStrategySignal(signalId);
  const subscriptions = useMySubscriptions();
  const { data: strategies } = useTradingStrategies();

  if (query.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const notFound = (query.error as ApiError | null)?.status === 404;

    return (
      <CAlert color={notFound ? "warning" : "danger"}>
        {notFound ? t("detail.notFound") : t("detail.loadError")}{" "}
        <Link to={routePaths.signals}>{t("detail.backToSignals")}</Link>
      </CAlert>
    );
  }

  const signal = query.data;
  const strategy = signal.tradingStrategyName;
  const direction = strategies?.find((s) => s.id === signal.tradingStrategyId)?.direction;
  // A strategy a membership unlocks has no subscription page to manage, so it opens the strategy's own page.
  const subscribed = customerHasActiveSubscription(
    signal.tradingStrategyId,
    subscriptions.data ?? [],
  );
  const strategyPath = subscribed
    ? `/subscriptions/${signal.tradingStrategyId}`
    : myStrategyDetailPath(signal.tradingStrategyId);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <SignalActionBadge action={signal.action} />
            <h1 className="mb-0">{signal.symbol}</h1>
          </div>
          <p className="text-body-secondary mb-0 mt-1">
            {strategy} ·{" "}
            {direction ? (
              <>
                <StrategySideLabel direction={direction} className="align-text-bottom" /> ·{" "}
              </>
            ) : null}
            {formatDateTimeFull(signal.receivedAt)}
          </p>
        </div>
        <div className="d-flex gap-2">
          <StepLink signalId={signal.olderSignalId} label={t("detail.older")} direction="older" />
          <StepLink signalId={signal.newerSignalId} label={t("detail.newer")} direction="newer" />
        </div>
      </div>

      <StrategyStanding signal={signal} />

      <CRow xs={{ gutter: 4 }} className="mb-4">
        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader>{t("detail.signalTitle")}</CCardHeader>
            <CCardBody>
              <dl className="row mb-0">
                <Fact label={t("detail.strategy")}>
                  <Link to={strategyPath}>{strategy}</Link>
                  <div className="small mt-1">
                    <StrategySideLabel direction={direction} />
                  </div>
                </Fact>
                <Fact label={t("detail.action")}>
                  {signalActionLabel(signal.action, t)}
                  <ActionNote action={signal.action} />
                </Fact>
                <Fact label={t("detail.instrument")}>
                  <span className="font-monospace">{signal.symbol}</span>
                </Fact>
                <Fact label={t("detail.received")}>{formatDateTimeFull(signal.receivedAt)}</Fact>
              </dl>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={6}>
          <TradeCard signal={signal} />
        </CCol>
      </CRow>

      <div className="text-uppercase small fw-bold text-body-secondary mb-2">
        {t("detail.recentTitle", { strategy })}
      </div>
      <div className="list-group mb-3">
        {signal.recentSignals.map((recent) => (
          <SignalRow
            key={recent.id}
            signal={recent}
            caption={recent.id === signal.id ? t("row.thisSignal") : ""}
            current={recent.id === signal.id}
          />
        ))}
      </div>

      <p className="small text-body-secondary mb-0">{t("detail.sameForEveryone", { strategy })}</p>
    </div>
  );
}

/**
 * Where the strategy stands. On its latest signal that is now, in the accent; on an older one it is
 * what has happened since, kept quiet so an old signal never reads as something to act on.
 */
function StrategyStanding({ signal }: { signal: TradingStrategySignalDetailDto }) {
  const { t } = useTranslation("signals");
  const strategy = signal.tradingStrategyName;
  const latest = signal.latestSignal;

  if (latest === null) {
    return (
      <div className="tinted-info rounded p-3 mb-4">
        <Trans
          t={t}
          i18nKey={standingNowKey(signal.action)}
          values={{ strategy, symbol: signal.symbol }}
          components={{ strong: <strong /> }}
        />
      </div>
    );
  }

  const closedBy = signal.trade.closedBy;
  const values = {
    strategy,
    latest: `${signalActionLabel(latest.action, t)} ${latest.symbol}`,
    time: formatSignalTime(latest.receivedAt),
    date: closedBy ? formatDayMonth(closedBy.receivedAt) : "",
  };

  return (
    <div className="bg-body-tertiary border rounded p-3 mb-4 d-flex flex-wrap justify-content-between align-items-center column-gap-3 row-gap-1">
      <span>
        <Trans
          t={t}
          i18nKey={closedBy ? "status.tradeClosed" : "status.signalledSince"}
          values={values}
          components={{ strong: <strong /> }}
        />
      </span>
      <Link to={signalDetailPath(latest.id)}>{t("status.seeLatest")}</Link>
    </div>
  );
}

function standingNowKey(action: number): string {
  switch (action) {
    case TradingStrategySignalAction.Buy:
      return "status.inMarket";
    case TradingStrategySignalAction.Exit:
      return "status.outOfMarket";
    default:
      return "status.latest";
  }
}

/** What the action does to the strategy's trade. */
function ActionNote({ action }: { action: number }) {
  const { t } = useTranslation("signals");

  if (action === TradingStrategySignalAction.Buy) {
    return <span className="fw-normal text-body-secondary"> · {t("detail.opensATrade")}</span>;
  }

  if (action === TradingStrategySignalAction.Exit) {
    return <span className="fw-normal text-body-secondary"> · {t("detail.closesTheTrade")}</span>;
  }

  return null;
}

/** The trade this signal opens or closes, read from whichever end the signal is. */
function TradeCard({ signal }: { signal: TradingStrategySignalDetailDto }) {
  const { t } = useTranslation("signals");
  const trade = signal.trade;
  const isBuy = signal.action === TradingStrategySignalAction.Buy;
  const isExit = signal.action === TradingStrategySignalAction.Exit;
  const openedAt = isBuy ? signal.receivedAt : trade.openedBy?.receivedAt;
  const closedAt = isExit ? signal.receivedAt : trade.closedBy?.receivedAt;

  return (
    <CCard className="h-100">
      <CCardHeader>{t("trade.title")}</CCardHeader>
      <CCardBody>
        <dl className="row mb-0">
          <Fact label={t("trade.opened")}>
            <TradeOpened signal={signal} />
          </Fact>
          <Fact label={t("trade.closed")}>
            <TradeClosed signal={signal} />
          </Fact>
          {openedAt && closedAt && (
            <Fact label={t("trade.held")}>{heldFor(openedAt, closedAt, t)}</Fact>
          )}
          {isBuy && trade.stillOpen && (
            <Fact label={t("trade.openFor")}>{openFor(signal.receivedAt, t)}</Fact>
          )}
        </dl>
      </CCardBody>
    </CCard>
  );
}

function TradeOpened({ signal }: { signal: TradingStrategySignalDetailDto }) {
  const { t } = useTranslation("signals");

  if (signal.action === TradingStrategySignalAction.Buy) {
    return <span className="text-primary">{t("trade.thisSignal")}</span>;
  }

  if (signal.trade.openedBy) {
    return <RelatedSignalLink signal={signal.trade.openedBy} />;
  }

  if (signal.trade.openedBeforeSignalsStarted) {
    return <>{t("trade.beforeSignalsStarted", { strategy: signal.tradingStrategyName })}</>;
  }

  return <>{t("trade.notKnown")}</>;
}

function TradeClosed({ signal }: { signal: TradingStrategySignalDetailDto }) {
  const { t } = useTranslation("signals");

  if (signal.action === TradingStrategySignalAction.Exit) {
    return <span className="text-primary">{t("trade.thisSignal")}</span>;
  }

  if (signal.trade.closedBy) {
    return <RelatedSignalLink signal={signal.trade.closedBy} />;
  }

  if (signal.trade.stillOpen) {
    return <>{t("trade.stillOpen")}</>;
  }

  return <>{t("trade.notKnown")}</>;
}

/** "Buy FNGD · Nov 2 · 15:58", linking to that signal's own page. */
function RelatedSignalLink({ signal }: { signal: RelatedTradingStrategySignalDto }) {
  const { t } = useTranslation("signals");

  return (
    <>
      <Link to={signalDetailPath(signal.id)}>
        {signalActionLabel(signal.action, t)} {signal.symbol}
      </Link>
      <span className="fw-normal text-body-secondary"> · {formatSignalTime(signal.receivedAt)}</span>
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 fw-normal text-body-secondary">{label}</dt>
      <dd className="col-sm-8 fw-semibold">{children}</dd>
    </>
  );
}

/** Steps through the same strategy's signals. Stays in place, disabled, at either end. */
function StepLink({
  signalId,
  label,
  direction,
}: {
  signalId: string | null;
  label: string;
  direction: "older" | "newer";
}) {
  const content = (
    <>
      {direction === "older" && <CIcon icon={cilChevronLeft} size="sm" className="me-1" />}
      {label}
      {direction === "newer" && <CIcon icon={cilChevronRight} size="sm" className="ms-1" />}
    </>
  );

  if (signalId === null) {
    return (
      <CButton color="secondary" variant="outline" size="sm" disabled>
        {content}
      </CButton>
    );
  }

  return (
    <Link to={signalDetailPath(signalId)} className="btn btn-outline-secondary btn-sm">
      {content}
    </Link>
  );
}

export default SignalDetail;
